import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Stethoscope, UserPlus, Search, Mail, Phone, 
  MoreVertical, Trash2, Edit, Users, Loader2,
  CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  PlayCircle, PauseCircle, XCircle, User
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  enrolled: { label: "Enrolled", color: "bg-blue-100 text-blue-700", icon: UserPlus },
  ongoing: { label: "Ongoing", color: "bg-green-100 text-green-700", icon: PlayCircle },
  suspended: { label: "Suspended", color: "bg-yellow-100 text-yellow-700", icon: PauseCircle },
  discharged: { label: "Discharged", color: "bg-gray-100 text-gray-600", icon: XCircle },
};

const PartnerPhysicians = () => {
  const { token } = useAuth();
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingPhysician, setAddingPhysician] = useState(false);
  const [expandedPhysician, setExpandedPhysician] = useState(null);
  const [physicianPatients, setPhysicianPatients] = useState({});
  const [loadingPatients, setLoadingPatients] = useState(null);
  const [newPhysician, setNewPhysician] = useState({
    name: "",
    email: "",
    phone: "",
    specialty: "",
    password: "",
  });

  useEffect(() => {
    fetchPhysicians();
  }, [token]);

  const fetchPhysicians = async () => {
    try {
      const response = await fetch(`${API}/partner-admin/physicians`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhysicians(data.physicians || []);
      }
    } catch (error) {
      console.error("Failed to fetch physicians:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhysician = async () => {
    if (!newPhysician.name || !newPhysician.email) {
      toast.error("Name and email are required");
      return;
    }

    setAddingPhysician(true);
    try {
      const response = await fetch(`${API}/partner-admin/physicians`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify(newPhysician),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.credentials) {
          toast.success(`Physician created! Login: ${data.credentials.email} / ${data.credentials.password}`);
        } else {
          toast.success(data.message || "Physician added successfully!");
        }
        setShowAddModal(false);
        setNewPhysician({ name: "", email: "", phone: "", specialty: "", password: "" });
        fetchPhysicians();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add physician");
      }
    } catch (error) {
      toast.error("Failed to add physician");
    } finally {
      setAddingPhysician(false);
    }
  };

  const handleRemovePhysician = async (physicianId) => {
    if (!confirm("Are you sure you want to remove this physician?")) return;

    try {
      const response = await fetch(`${API}/partner-admin/physicians/${physicianId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success("Physician removed");
        fetchPhysicians();
      } else {
        toast.error("Failed to remove physician");
      }
    } catch (error) {
      toast.error("Failed to remove physician");
    }
  };

  const fetchPhysicianPatients = async (physicianId) => {
    if (expandedPhysician === physicianId) {
      setExpandedPhysician(null);
      return;
    }
    
    setLoadingPatients(physicianId);
    try {
      const response = await fetch(`${API}/partner-admin/physicians/${physicianId}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhysicianPatients(prev => ({
          ...prev,
          [physicianId]: data
        }));
        setExpandedPhysician(physicianId);
      } else {
        toast.error("Failed to load patients");
      }
    } catch (error) {
      console.error("Failed to fetch physician patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoadingPatients(null);
    }
  };

  const filteredPhysicians = physicians.filter(
    (p) => {
      const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.specialty?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (statusFilter === 'active') return p.status === 'active';
      if (statusFilter === 'pending') return p.status === 'pending';
      return true;
    }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Physicians</h1>
          <p className="text-muted-foreground">Manage your organization's physicians and dietitians</p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-green-500 hover:bg-green-600"
          data-testid="add-physician-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Physician
        </Button>
      </div>

      {/* Stats - Clickable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
        <Card 
          className={`bg-white cursor-pointer transition-all hover:shadow-md ${statusFilter === null ? 'ring-2 ring-blue-200' : ''}`}
          onClick={() => setStatusFilter(statusFilter === null ? null : null)}
          data-testid="stat-total-physicians"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{physicians.length}</p>
              <p className="text-sm text-muted-foreground">Total Physicians</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-white cursor-pointer transition-all hover:shadow-md ${statusFilter === 'active' ? 'ring-2 ring-green-400' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'active' ? null : 'active')}
          data-testid="stat-active-physicians"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {physicians.filter(p => p.status === "active").length}
              </p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-white cursor-pointer transition-all hover:shadow-md ${statusFilter === 'pending' ? 'ring-2 ring-orange-400' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
          data-testid="stat-pending-physicians"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {physicians.filter(p => p.status === "pending").length}
              </p>
              <p className="text-sm text-muted-foreground">Pending Invites</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Badge */}
      {statusFilter && (
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
            Filtered: {statusFilter === 'active' ? 'Active' : 'Pending'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter(null)} className="text-xs text-muted-foreground">
            Clear
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search physicians..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-physicians"
        />
      </div>

      {/* Physicians List */}
      <Card className="bg-white">
        <CardContent className="p-0">
          {filteredPhysicians.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary mb-2">No Physicians Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add physicians to your organization to start managing patients together.
              </p>
              <Button onClick={() => setShowAddModal(true)} className="bg-green-500 hover:bg-green-600">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First Physician
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-4 sm:px-6 py-3 bg-gray-50 text-sm font-medium text-muted-foreground">
                <div className="col-span-4">Physician</div>
                <div className="col-span-2">Specialty</div>
                <div className="col-span-2">Patients</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Actions</div>
              </div>
              
              {/* Table Rows */}
              {filteredPhysicians.map((physician) => (
                <div key={physician.user_id}>
                  <div 
                    className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 px-4 sm:px-6 py-3 sm:py-4 md:items-center hover:bg-gray-50 cursor-pointer"
                    onClick={() => fetchPhysicianPatients(physician.user_id)}
                  >
                    <div className="md:col-span-4 flex items-center gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                        <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-secondary text-sm truncate">{physician.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{physician.email}</p>
                      </div>
                    </div>
                    <div className="md:col-span-2 flex md:block items-center gap-2 pl-12 md:pl-0">
                      <span className="text-xs md:text-sm text-muted-foreground md:text-secondary">
                        {physician.specialty || "General"}
                      </span>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-2 pl-12 md:pl-0">
                      <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
                      <span className="text-xs sm:text-sm text-secondary">{physician.patient_count || 0}</span>
                    </div>
                    <div className="md:col-span-2 pl-12 md:pl-0">
                      <Badge 
                        variant={physician.status === "active" ? "default" : "secondary"}
                        className={physician.status === "active" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}
                      >
                        {physician.status === "active" ? "Active" : "Pending"}
                      </Badge>
                    </div>
                    <div className="md:col-span-2 flex gap-2 items-center pl-12 md:pl-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhysician(physician.user_id);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {loadingPatients === physician.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      ) : (
                        expandedPhysician === physician.user_id ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Patient List */}
                  {expandedPhysician === physician.user_id && physicianPatients[physician.user_id] && (
                    <div className="bg-gray-50 px-6 py-4 border-t">
                      <div className="ml-12 space-y-4">
                        <h4 className="font-medium text-secondary text-sm">
                          Patients by Status ({physicianPatients[physician.user_id].total || 0} total)
                        </h4>
                        
                        {/* Status Groups */}
                        {Object.entries(STATUS_CONFIG).map(([statusKey, statusConfig]) => {
                          const patients = physicianPatients[physician.user_id].patients_by_status?.[statusKey] || [];
                          if (patients.length === 0) return null;
                          
                          const StatusIcon = statusConfig.icon;
                          
                          return (
                            <div key={statusKey} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <StatusIcon className={`w-4 h-4 ${statusConfig.color.replace('bg-', 'text-').split(' ')[1]}`} />
                                <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${statusConfig.color}`}>
                                  {statusConfig.label} ({patients.length})
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                                {patients.map((patient) => (
                                  <div 
                                    key={patient.user_id}
                                    className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100"
                                  >
                                    <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-blue-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-secondary truncate">
                                        {patient.name || "Unknown"}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {patient.email}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        
                        {physicianPatients[physician.user_id].total === 0 && (
                          <p className="text-sm text-muted-foreground italic">
                            No patients assigned to this physician yet.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Physician Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Add New Physician
            </DialogTitle>
            <DialogDescription>
              Add a physician to your organization. They will receive an email invitation to join.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="Dr. John Smith"
                value={newPhysician.name}
                onChange={(e) => setNewPhysician({ ...newPhysician, name: e.target.value })}
                data-testid="physician-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@hospital.com"
                value={newPhysician.email}
                onChange={(e) => setNewPhysician({ ...newPhysician, email: e.target.value })}
                data-testid="physician-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                placeholder="+1 234 567 8900"
                value={newPhysician.phone}
                onChange={(e) => setNewPhysician({ ...newPhysician, phone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="specialty">Specialty (Optional)</Label>
              <Input
                id="specialty"
                placeholder="e.g., Endocrinology, Nutrition"
                value={newPhysician.specialty}
                onChange={(e) => setNewPhysician({ ...newPhysician, specialty: e.target.value })}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-secondary mb-2">Login Credentials</p>
              <div className="space-y-2">
                <Label htmlFor="password">Password (Optional)</Label>
                <Input
                  id="password"
                  type="text"
                  placeholder="Set initial login password"
                  value={newPhysician.password}
                  onChange={(e) => setNewPhysician({ ...newPhysician, password: e.target.value })}
                  data-testid="physician-password"
                />
                <p className="text-xs text-muted-foreground">
                  If left empty, a temporary password will be generated. Share the credentials with the physician.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddPhysician} 
              disabled={addingPhysician}
              className="bg-green-500 hover:bg-green-600"
            >
              {addingPhysician ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Physician
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerPhysicians;
